import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Message,
  Segment,
  Header,
  Divider,
  Button,
  Icon,
  Grid
} from 'semantic-ui-react';
import {
  fetchUserProfiles,
  selectUserProfiles,
  createAdditionalProfile
} from '../slices/userRoleSlice';
import AdminProfileForm from './AdminProfileForm';
import BookCreatorProfileForm from './BookCreatorProfileForm';
import VoiceActorProfileForm from './VoiceActorProfileForm';
import InvalidProfileRequest from './InvalidProfileRequest';
import useRoleNormalization from '../hooks/useRoleNormalization';

/**
 * ProfileManager component for managing user profiles
 * @param {Object} props - Component props
 * @param {string} props.userId - User ID
 * @param {string} props.userRole - (Optional) User's primary role - used in admin mode
 * @param {Object} props.initialUserData - (Optional) Initial data for new profiles
 * @returns {JSX.Element} ProfileManager component
 */
const ProfileManager = ({ userId, userRole = null, initialUserData = null }) => {
  const dispatch = useDispatch();
  const profiles = useSelector(selectUserProfiles);

  console.log('=== PROFILES FROM REDUX ===', profiles);

  // Initialize role normalization hook
  const {
    normalizeRole,
    getRoleProfiles,
    getRoleIcon,
    getRoleLabel,
    isValidRole,
    extractRoleFromProfileType,
    filterProfilesForRole
  } = useRoleNormalization();

  const [activeProfileIndex, setActiveProfileIndex] = useState(0);
  const [error, setError] = useState(null);
  const [hasManuallySelected, setHasManuallySelected] = useState(false);

  // Determine if we're in admin mode (editing another user)
  const currentUserId = useSelector(state => state.user.profile?.data?.id);
  const isAdminMode = userId && currentUserId && userId !== currentUserId;

  // Get the user's primary role - NEVER fallback to logged-in user's role when in admin mode
  const reduxPrimaryRole = useSelector(state => state.user.profile?.data?.primaryRole);
  let primaryRole = null;

  if (isAdminMode) {
    // ADMIN MODE: Must have userRole prop, no fallbacks
    primaryRole = userRole;
    if (!primaryRole) {
      console.error('ProfileManager: userRole prop is required when editing another user');
    }
  } else {
    // SELF-EDIT MODE: Use prop if provided, otherwise use Redux
    primaryRole = userRole || reduxPrimaryRole;
  }

  // Debug logging to verify role detection
  console.log('=== ROLE DETECTION ===');
  console.log('userRole prop:', userRole);
  console.log('reduxPrimaryRole:', reduxPrimaryRole);
  console.log('isAdminMode:', isAdminMode);
  console.log('primaryRole being used:', primaryRole);

  // Get the loading state from Redux
  const isLoading = useSelector(state => state.user.userRole.status === 'loading');

  // Use a ref to track if we've already attempted to create profiles for each type
  const profileCreationAttempted = useRef({
    admin: false,
    bookCreator: false,
    voiceActor: false
  });

  // Fetch user profiles when component mounts
  useEffect(() => {
    if (userId) {
      dispatch(fetchUserProfiles(userId));
    }
  }, [userId, dispatch]);

  // Reset active index if it's out of bounds when profiles change
  useEffect(() => {
    if (profiles && profiles.length > 0) {
      // If current index is out of bounds, reset to 0
      if (activeProfileIndex >= profiles.length) {
        console.log(`Index ${activeProfileIndex} out of bounds for ${profiles.length} profiles, resetting to 0`);
        setActiveProfileIndex(0);
        return;
      }

      // Validate the profile at current index is allowed for this role
      if (primaryRole) {
        const allowedProfiles = getRoleProfiles(primaryRole);
        const currentProfile = profiles[activeProfileIndex];
        if (currentProfile) {
          const profileType = currentProfile.type ||
            extractRoleFromProfileType(currentProfile.profileType);

          if (!allowedProfiles.includes(profileType)) {
            // Current profile is not allowed for this role, find first valid profile
            const firstValidIndex = profiles.findIndex(p => {
              const pType = p.type || extractRoleFromProfileType(p.profileType);
              return allowedProfiles.includes(pType);
            });

            if (firstValidIndex !== -1) {
              console.log(`Current profile ${profileType} not allowed for ${primaryRole}, switching to index ${firstValidIndex}`);
              setActiveProfileIndex(firstValidIndex);
            }
          }
        }
      }
    }
  }, [profiles, primaryRole, activeProfileIndex, getRoleProfiles, extractRoleFromProfileType]);

  // Auto-select the correct profile based on user's primary role - ONLY on initial load
  useEffect(() => {
    // Skip auto-selection if user has manually selected a profile
    if (hasManuallySelected) {
      console.log('=== SKIPPING AUTO-SELECT: User has manually selected a profile ===');
      return;
    }

    if (profiles && profiles.length > 0 && primaryRole) {
      // Find the index of the profile matching the user's primary role
      const primaryProfileIndex = profiles.findIndex(p => {
        const profileType = p.type || extractRoleFromProfileType(p.profileType);
        return profileType === primaryRole;
      });

      if (primaryProfileIndex !== -1 && primaryProfileIndex !== activeProfileIndex) {
        console.log(`=== AUTO-SELECTING PROFILE (Initial Load) ===`);
        console.log(`Setting active profile to ${primaryRole} at index ${primaryProfileIndex}`);
        setActiveProfileIndex(primaryProfileIndex);
      }
    }
  }, [profiles, primaryRole, extractRoleFromProfileType, hasManuallySelected]); // Include hasManuallySelected to respect manual selections

  // Function to create a profile with initial data
  const createProfileForRole = async (role) => {
    if (!profileCreationAttempted.current[role]) {
      profileCreationAttempted.current[role] = true;
      console.log(`Creating ${role} profile automatically`);

      try {
        // Determine initial data source
        let initialData = {};

        // Priority 1: Use initialUserData if provided (from AddUser flow)
        if (initialUserData && (initialUserData.firstName || initialUserData.lastName)) {
          initialData = {
            firstName: initialUserData.firstName || '',
            lastName: initialUserData.lastName || '',
            street: initialUserData.street || '',
            city: initialUserData.city || '',
            state: initialUserData.state || '',
            zipcode: initialUserData.zipcode || initialUserData.zipCode || '',
            country: initialUserData.country || 'USA',
            phones: initialUserData.phone ? [initialUserData.phone] : (initialUserData.phones || [])
          };
          console.log(`Using initial user data for ${role} profile`);
        }
        // Priority 2: Copy from existing profile if available
        else if (profiles && profiles.length > 0) {
          const sourceProfile = profiles.find(p => p.isPrimary) || profiles[0];
          if (sourceProfile && (sourceProfile.firstName || sourceProfile.lastName)) {
            initialData = {
              firstName: sourceProfile.firstName || '',
              lastName: sourceProfile.lastName || '',
              street: sourceProfile.street || '',
              city: sourceProfile.city || '',
              state: sourceProfile.state || '',
              zipcode: sourceProfile.zipcode || sourceProfile.zipCode || '',
              country: sourceProfile.country || 'USA',
              phones: sourceProfile.phones || []
            };
            console.log(`Copying data from existing profile for ${role} profile`);
          }
        }

        console.log(`Creating ${role} profile with initial data:`, initialData);

        await dispatch(createAdditionalProfile({
          userId,
          profileType: role,
          profileData: initialData
        })).unwrap();

        console.log(`${role} profile created successfully`);
        // Refresh profiles after successful creation
        dispatch(fetchUserProfiles(userId));
      } catch (error) {
        console.error(`Failed to create ${role} profile:`, error);

        // Extract the actual error message from the error object
        const errorMessage = error?.error || error?.message || error;

        // Handle "already exists" error - check the actual message string
        if (typeof errorMessage === 'string' && errorMessage.includes('already has a')) {
          console.log(`${role} profile already exists, refreshing profiles`);
          dispatch(fetchUserProfiles(userId));
          // Don't show this as an error to the user since it's expected
          setError(null);
        } else {
          // Reset attempt flag on real errors to allow retry
          profileCreationAttempted.current[role] = false;
          // Use the extracted error message for display
          setError(`Failed to create ${role} profile: ${errorMessage}`);
        }
      }
    }
  };

  // Auto-create all allowed profiles based on user role - STRICT enforcement
  useEffect(() => {
    const createMissingProfiles = async () => {
      console.log('=== PROFILE CREATION LOGIC ===');
      console.log('Primary role for profile creation:', primaryRole);

      // Only proceed if we have all required data
      if (!userId || !primaryRole || profiles === null || isLoading) {
        return;
      }

      // Validate role first
      if (!isValidRole(primaryRole)) {
        console.error(`Invalid role: ${primaryRole}`);
        setError(`Invalid user role: ${primaryRole}`);
        return;
      }

      // Get ONLY the profiles this role should have from roleConfig
      const expectedProfiles = getRoleProfiles(primaryRole);
      console.log(`User with role ${primaryRole} should have exactly these profiles:`, expectedProfiles);

      // Check and create each expected profile - NO EXTRAS
      for (const profileType of expectedProfiles) {
        const hasProfile = profiles.some(p => {
          const pType = p.type || extractRoleFromProfileType(p.profileType);
          return pType === profileType;
        });

        console.log(`Checking ${profileType} profile: ${hasProfile ? 'exists' : 'missing'}`);

        if (!hasProfile && !profileCreationAttempted.current[profileType]) {
          console.log(`Creating missing ${profileType} profile for ${primaryRole} user`);
          await createProfileForRole(profileType);
        }
      }

      // Validate no extra profiles exist (log warning but don't delete)
      const actualProfileTypes = profiles.map(p =>
        p.type || extractRoleFromProfileType(p.profileType)
      );
      const extraProfiles = actualProfileTypes.filter(
        type => !expectedProfiles.includes(type)
      );

      if (extraProfiles.length > 0) {
        console.warn(`User with role ${primaryRole} has unexpected profiles: ${extraProfiles.join(', ')}`);
        console.warn('These profiles should be removed from the database');
      }
    };

    createMissingProfiles();
  }, [userId, primaryRole, profiles, isLoading, dispatch, initialUserData, getRoleProfiles, isValidRole, extractRoleFromProfileType]);

  // Render the active profile component
  const renderActiveProfile = () => {
    console.log('=== RENDER ACTIVE PROFILE ===');
    console.log('Active Index:', activeProfileIndex);
    console.log('Profiles array:', profiles);

    // Validate profiles exist
    if (!profiles || profiles.length === 0) {
      if (isLoading) {
        return <Message info>Loading profiles...</Message>;
      }
      return <Message error>No profiles found for this user.</Message>;
    }

    // Validate index is within bounds
    if (activeProfileIndex >= profiles.length || activeProfileIndex < 0) {
      console.error(`Invalid profile index: ${activeProfileIndex} for ${profiles.length} profiles`);
      // Reset to 0 as a recovery mechanism
      setActiveProfileIndex(0);
      return <Message warning>Profile index error detected. Resetting...</Message>;
    }

    const activeProfile = profiles[activeProfileIndex];
    console.log('Active Profile:', activeProfile);
    console.log('>>> DEBUG: activeProfile.type:', activeProfile?.type);
    console.log('>>> DEBUG: activeProfile.profileType:', activeProfile?.profileType);
    console.log('>>> DEBUG: activeProfile.uuid:', activeProfile?.uuid);

    // This should never happen now, but validate
    if (!activeProfile) {
      console.error('Active profile is undefined despite bounds check');
      return <Message error>Profile data error. Please refresh the page.</Message>;
    }

    // Extract profile type using the hook's logic
    const profileType = activeProfile.type ||
      extractRoleFromProfileType(activeProfile.profileType);

    console.log('>>> DEBUG: Extracted profileType:', profileType);

    if (!profileType) {
      console.error('Could not determine profile type from:', activeProfile);
      return <Message error>Invalid profile type detected.</Message>;
    }

    // Handle profile creation in progress
    if (!activeProfile.uuid) {
      return <Message info>Setting up your {getRoleLabel(profileType)} profile...</Message>;
    }

    const handleSave = () => {
      dispatch(fetchUserProfiles(userId));
    };

    switch (profileType) {
      case 'admin':
        console.log('Rendering AdminProfileForm');
        return <AdminProfileForm
          profile={activeProfile}
          userId={userId}
          initialUserData={initialUserData}
          onSave={handleSave}
        />;
      case 'bookCreator':
        console.log('Rendering BookCreatorProfileForm');
        return <BookCreatorProfileForm
          profile={activeProfile}
          userId={userId}
          initialUserData={initialUserData}
          onSave={handleSave}
        />;
      case 'voiceActor':
        console.log('Rendering VoiceActorProfileForm');
        return <VoiceActorProfileForm
          profile={activeProfile}
          userId={userId}
          initialUserData={initialUserData}
          onSave={handleSave}
        />;
      default:
        console.log('Rendering InvalidProfileRequest for type:', profileType);
        return <InvalidProfileRequest profileType={profileType} />;
    }
  };

  // Early return if no valid role
  if (!primaryRole && !isLoading) {
    return (
      <Container>
        <Segment>
          <Message error>
            <Message.Header>Role Detection Error</Message.Header>
            <p>Unable to determine user role. Please refresh the page or contact support.</p>
          </Message>
        </Segment>
      </Container>
    );
  }

  return (
    <Container>
      <Segment>
        <Header as="h1">Profile Management</Header>
        <Divider />

        {error && <Message error content={error} />}

        {/* Profile selection buttons - ONLY show allowed profiles */}
        {profiles && profiles.length > 0 && primaryRole && (
          <div className="profile-selector" style={{ marginBottom: '20px' }}>
            <Grid>
              <Grid.Row>
                {(() => {
                  // Filter to only allowed profiles for this role
                  const allowedProfiles = getRoleProfiles(primaryRole);
                  const validProfiles = profiles.filter((profile) => {
                    const profileType = profile.type ||
                      extractRoleFromProfileType(profile.profileType);
                    const isAllowed = allowedProfiles.includes(profileType);

                    if (!isAllowed) {
                      console.warn(`Hiding disallowed profile ${profileType} for role ${primaryRole}`);
                    }
                    return isAllowed;
                  });

                  return validProfiles.map((profile) => {
                    const originalIndex = profiles.indexOf(profile);
                    const profileType = profile.type ||
                      extractRoleFromProfileType(profile.profileType);

                    console.log(`>>> BUTTON MAP: ${profileType} button -> index ${originalIndex}`);
                    console.log(`>>> BUTTON MAP: profile at index ${originalIndex}:`, profiles[originalIndex]);

                    return (
                      <Grid.Column key={`${profileType}-${profile.uuid || originalIndex}`} width={4}>
                        <Button
                          fluid
                          icon
                          labelPosition="left"
                          color={activeProfileIndex === originalIndex ? 'blue' : 'grey'}
                          onClick={() => {
                            console.log(`=== BUTTON CLICKED ===`);
                            console.log(`Button for: ${profileType}`);
                            console.log(`Setting activeProfileIndex to: ${originalIndex}`);
                            console.log(`Profile at index ${originalIndex}:`, profiles[originalIndex]);
                            console.log(`Marking as manually selected`);
                            setHasManuallySelected(true); // Mark that user has made a manual selection
                            setActiveProfileIndex(originalIndex);
                          }}
                          style={{ marginBottom: '10px', position: 'relative', zIndex: 10 }}
                        >
                          <Icon name={getRoleIcon(profileType)} />
                          {getRoleLabel(profileType)}
                          {profile.isPrimary ? ' (Primary)' : ''}
                        </Button>
                      </Grid.Column>
                    );
                  });
                })()}
              </Grid.Row>
            </Grid>
          </div>
        )}

        {/* Render the active profile component */}
        <div className="profile-content" style={{ minHeight: '500px' }}>
          {renderActiveProfile()}
        </div>
      </Segment>
    </Container>
  );
};

export default ProfileManager;
